import {validateChannelName, AudioClip} from "../synth/synth.js";
import { Song, Channel, Instrument, Pattern, Note } from "../synth/synth.js";
import { Config } from "../synth/SynthConfig.js";

import {checkKeys, validateInstrument} from "./SoundLibrary.js";

const names = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const drumNames = ["kick", "low floor tom", "low tom", "mid tom", "snare", "clap", "ride", "crash", "hi-hat", "splash", "cabasa", "tambourine"];
export function pitchName(midi: number): string { return names[((midi % 12) + 12) % 12] + (Math.floor(midi / 12) - 1); }
function fail(message: string): never { throw new Error(message); }
function integer(value: any, min: number, max: number, path: string): number {
    if (!Number.isInteger(value) || value < min || value > max) fail(`${path}: expected an integer from ${min} to ${max}.`);
    return value;
}
function array(value: any, min: number, max: number, path: string): any[] {
    if (!Array.isArray(value) || value.length < min || value.length > max) fail(`${path}: expected ${min}–${max} entries.`);
    return value;
}
function object(value: any, path: string): any {
    if (!value || typeof value !== "object" || Array.isArray(value)) fail(`${path}: expected an object.`);
    return value;
}
function boolean(value: any, path: string): boolean {
    if (typeof value !== "boolean") fail(`${path}: expected true or false.`);
    return value;
}
function pitch(value: any, noise: boolean, key: number, path: string): number {
    if (typeof value !== "string") fail(`${path}: use a note name such as C4, or D0–D11 for drums.`);
    if (noise) {
        const match = /^D(\d+)$/.exec(value);
        if (!match) fail(`${path}: drum pitches must be D0–D11.`);
        return integer(Number(match![1]), 0, Config.drumCount - 1, path);
    }
    const match = /^([A-G])([#b♯♭]?)(-?\d+)$/.exec(value);
    if (!match) fail(`${path}: invalid pitch ${value}. Use C4, F#4, or Bb3.`);
    const accidental = match![2];
    const midi = (Number(match![3]) + 1) * 12 + [0, 2, 4, 5, 7, 9, 11]["CDEFGAB".indexOf(match![1])] + (accidental === "#" || accidental === "♯" ? 1 : accidental ? -1 : 0);
    return integer(midi - key - 12, 0, Config.maxPitch, path);
}

/** A score, not a transcript of the rendered audio. No hidden native song payload. */
export function songToText(song: Song, label?: (instrument: Instrument) => string): string {
    return JSON.stringify({
        format: "Bop score", version: 1,
        guide: {
            timing: "24 ticks = one quarter-note beat. Note at/length are ticks within a bar; rests are gaps. Bars, patterns, and instruments are numbered from 1. Sequence 0 means silence.",
            pitches: "Written note names use MIDI C4=60, before instrument shifts/effects. Key changes do not transpose these absolute names. Drum slots are D0–D11 (sound depends on instrument).",
            pins: "[offsetTicks, bendSemitones, expression0to3]. First offset=0, first bend=0; last offset=length. Omit pins for a steady note at expression 3.",
        },
        tempo: song.tempo, key: names[song.key], scale: song.scale === 6 ? "major" : song.scale === 7 ? "minor" : Config.scales[song.scale].name,
        beatsPerBar: song.beatsPerBar, ticksPerBeat: 24, rhythm: Config.rhythms[song.rhythm].name,
        bars: song.barCount, loop: {startBar: song.loopStart + 1, length: song.loopLength},
        layeredInstruments: song.layeredInstruments, patternInstruments: song.patternInstruments,
        channels: song.channels.map((channel, i) => ({
            ...(channel.name ? {name: channel.name} : {}),
            label: channel.name || (label ? channel.instruments.map(label).join(" / ") : `Channel ${i + 1}`),
            type: song.getChannelIsNoise(i) ? "drum" : "pitch", octave: channel.octave,
            instruments: channel.instruments.map(instrument => instrument.toJsonObject()),
            sequence: channel.bars,
            // Recorded audio placements (the audio stays on the recording device).
            ...(channel.clips.length > 0 ? {recordings: channel.clips.map(clip => clip.toJsonObject())} : {}),
            patterns: channel.patterns.map(pattern => ({
                instruments: pattern.instruments.map(index => index + 1),
                notes: pattern.notes.map(note => ({
                    at: note.start, length: note.end - note.start,
                    pitches: note.pitches.map(p => song.getChannelIsNoise(i) ? `D${p}` : pitchName(p + song.key + 12)),
                    pins: note.pins.map(pin => [pin.time, pin.interval, pin.size]),
                    ...(note.continuesLastPattern ? {continues: true} : {}),
                })),
            })),
        })),
    }, null, 2).replace(/\[\n\s+((?:-?\d+(?:\.\d+)?|"[^"\\]*")(?:,\n\s+(?:-?\d+(?:\.\d+)?|"[^"\\]*"))*)\n\s+\]/g,
        (_match: string, items: string) => "[" + items.replace(/\n\s*/g, " ") + "]");
}

export function songFromText(text: string): Song {
    if (text.length > 4000000) fail("Score is too large (maximum 4 MB of text).");
    text = text.trim().replace(/^```(?:json)?\s*\n([\s\S]*)\n```$/, "$1");
    let data: any;
    try { data = JSON.parse(text); } catch { fail("Invalid JSON. Paste a complete Bop score with matching braces and quoted keys."); }
    object(data, "Score");
    checkKeys(data,["format","version","guide","tempo","key","scale","beatsPerBar","ticksPerBeat","rhythm","bars","loop","layeredInstruments","patternInstruments","channels"],"Score");
    if (data.format !== "Bop score" || data.version !== 1) fail('Expected "format": "Bop score" and "version": 1.');
    if (data.ticksPerBeat !== 24) fail("ticksPerBeat must be 24; do not rescale timing silently.");
    const song = new Song();
    song.tempo = integer(data.tempo, Config.tempoMin, Config.tempoMax, "tempo");
    song.key = names.indexOf(data.key);
    if (song.key < 0) fail("key: use C, C#, D, D#, E, F, F#, G, G#, A, A#, or B.");
    song.scale = data.scale === "major" ? 6 : data.scale === "minor" ? 7 : Config.scales.findIndex(scale => scale.name === data.scale);
    if (song.scale < 0) fail("scale: unknown scale name.");
    song.rhythm = Config.rhythms.findIndex(rhythm => rhythm.name === data.rhythm);
    if (song.rhythm < 0) fail("rhythm: unknown grid setting.");
    song.beatsPerBar = integer(data.beatsPerBar, Config.beatsPerBarMin, Config.beatsPerBarMax, "beatsPerBar");
    song.barCount = integer(data.bars, 1, Config.barCountMax, "bars");
    checkKeys(data.loop,["startBar","length"],"loop");
    song.loopStart = integer(data.loop.startBar, 1, song.barCount, "loop.startBar") - 1;
    song.loopLength = integer(data.loop.length, 1, song.barCount - song.loopStart, "loop.length");
    song.layeredInstruments = boolean(data.layeredInstruments, "layeredInstruments");
    song.patternInstruments = boolean(data.patternInstruments, "patternInstruments");
    const channels = array(data.channels, 1, 15, "channels");
    song.channels.length = 0; song.pitchChannelCount = 0; song.noiseChannelCount = 0;
    let seenNoise = false;
    channels.forEach((raw: any, i: number) => {
        const path = `Channel ${i + 1}`; checkKeys(raw,["name","label","type","octave","instruments","sequence","patterns","recordings"],path);
        if (raw.type !== "pitch" && raw.type !== "drum") fail(`${path}.type: use pitch or drum.`);
        const noise = raw.type === "drum";
        if (noise) { seenNoise = true; song.noiseChannelCount++; }
        else { if (seenNoise) fail("Pitch channels must come before drum channels."); song.pitchChannelCount++; }
        const channel = new Channel();
        if (raw.name !== undefined) channel.name = validateChannelName(raw.name);
        if (raw.recordings !== undefined) {
            channel.clips = array(raw.recordings, 0, 64, `${path}.recordings`).map((clip: any, c: number) => {
                const parsed = AudioClip.fromJsonObject(clip);
                if (parsed == null) fail(`${path}.recordings[${c + 1}]: needs take, start, offset and a positive duration.`);
                return parsed!;
            });
        }
        channel.octave = integer(raw.octave, 0, Config.pitchOctaves, `${path}.octave`);
        array(raw.instruments, 1, song.getMaxInstrumentsPerChannel(), `${path}.instruments`).forEach((settings: any) => {
            validateInstrument(settings, `${path}.instrument`);
            if (Config.instrumentTypeNames.indexOf(settings.type) < 0) fail(`${path}: unknown instrument type.`);
            const instrument = new Instrument(noise); instrument.fromJsonObject(settings, noise); channel.instruments.push(instrument);
        });
        array(raw.patterns, 1, 128, `${path}.patterns`).forEach((rawPattern: any, p: number) => {
            const where = `${path}, pattern ${p + 1}`; checkKeys(rawPattern,["instruments","notes"],where);
            const pattern = new Pattern();
            const instruments = array(rawPattern.instruments, 1, song.layeredInstruments ? Math.min(4, channel.instruments.length) : 1, `${where}.instruments`);
            pattern.instruments.splice(0, pattern.instruments.length, ...instruments.map(n => integer(n, 1, channel.instruments.length, `${where}.instrument`) - 1));
            if (new Set(pattern.instruments).size !== pattern.instruments.length) fail(`${where}: duplicate instruments.`);
            let end = 0;
            array(rawPattern.notes, 0, song.beatsPerBar * 24, `${where}.notes`).forEach((rawNote: any, n: number) => {
                const at = `${where}, note ${n + 1}`; checkKeys(rawNote,["at","length","pitches","pins","continues"],at);
                const start = integer(rawNote.at, 0, song.beatsPerBar * 24 - 1, `${at}.at`);
                const length = integer(rawNote.length, 1, song.beatsPerBar * 24 - start, `${at}.length`);
                if (start < end) fail(`${at}: notes must be ordered and non-overlapping. Put simultaneous pitches in one chord.`);
                end = start + length;
                const pitches = array(rawNote.pitches, 1, Config.maxChordSize, `${at}.pitches`).map(v => pitch(v, noise, song.key, at));
                if (new Set(pitches).size !== pitches.length) fail(`${at}: duplicate pitches.`);
                const note = new Note(pitches[0], start, end, 3); note.pitches = pitches;
                if (rawNote.pins !== undefined) {
                    let previous = -1;
                    note.pins = array(rawNote.pins, 2, length + 1, `${at}.pins`).map((pin: any) => {
                        array(pin, 3, 3, `${at}.pin`);
                        const time = integer(pin[0], 0, length, `${at}.pin offset`);
                        if (time <= previous) fail(`${at}: pin offsets must strictly increase.`);
                        previous = time;
                        const interval = integer(pin[1], -84, 84, `${at}.bend`);
                        pitches.forEach(p => integer(p + interval, 0, noise ? 11 : Config.maxPitch, `${at}.bent pitch`));
                        return {time, interval, size: integer(pin[2], 0, 3, `${at}.expression`)};
                    });
                    if (note.pins[0].time !== 0 || note.pins[0].interval !== 0 || note.pins[note.pins.length - 1].time !== length) fail(`${at}: first pin must be at 0 with bend 0; last pin must be at length.`);
                }
                if (rawNote.continues !== undefined) note.continuesLastPattern = boolean(rawNote.continues, `${at}.continues`);
                if (note.continuesLastPattern && start !== 0) fail(`${at}: a continuing note must start at tick 0.`);
                pattern.notes.push(note);
            });
            channel.patterns.push(pattern);
        });
        const sequence = array(raw.sequence, song.barCount, song.barCount, `${path}.sequence`);
        channel.bars.push(...sequence.map(id => integer(id, 0, channel.patterns.length, `${path}.sequence pattern`)));
        song.channels.push(channel);
    });
    integer(song.pitchChannelCount, 1, Config.pitchChannelCountMax, "Pitch channel count");
    integer(song.noiseChannelCount, 0, Config.noiseChannelCountMax, "Drum channel count");
    song.patternsPerChannel = Math.max(...song.channels.map(channel => channel.patterns.length));
    song.channels.forEach(channel => { while (channel.patterns.length < song.patternsPerChannel) channel.patterns.push(new Pattern()); });
    // Verify that the normal editor format can represent the constructed score.
    return new Song(song.toBase64String());
}

export function songReadout(song: Song, label?: (instrument: Instrument) => string): string {
    const lines = ["BOP MUSICAL READOUT", "A symbolic score, not an audio listening review. Written pitches precede instrument effects.",
        `${song.tempo} BPM | ${names[song.key]} ${song.scale === 6 ? "major" : song.scale === 7 ? "minor" : Config.scales[song.scale].name} | ${song.beatsPerBar}/4 | ${song.barCount} bars | ${(song.barCount * song.beatsPerBar * 60 / song.tempo).toFixed(1)} seconds (one pass)`,
        `Loop: bars ${song.loopStart + 1}–${song.loopStart + song.loopLength}. Pattern IDs are local to each channel; 0 means silence.`,
        "Timing below is in quarter-note beats from the start of each bar; + means duration. Gaps are rests.", ""];
    song.channels.forEach((channel, i) => {
        const noise = song.getChannelIsNoise(i);
        lines.push(`CHANNEL ${i + 1} (${noise ? "drums" : "pitched"})${channel.name ? ` — ${channel.name}` : ""}${channel.muted ? " [currently muted in editor]" : ""}`);
        channel.instruments.forEach((instrument, j) => {
            const settings: any = instrument.toJsonObject();
            lines.push(`  Instrument ${j + 1}: ${label ? label(instrument) : settings.type}; volume ${settings.volume}; effects: ${(settings.effects || []).join(", ") || "none"}.`);
        });
        lines.push(`  Arrangement: ${channel.bars.join(" ")}`);
        channel.patterns.forEach((pattern, p) => {
            const bars = channel.bars.map((id, b) => id === p + 1 ? b + 1 : 0).filter(b => b > 0);
            if (!bars.length) return;
            lines.push(`  Pattern ${p + 1} | bars ${bars.join(",")} | instruments ${pattern.instruments.map(n => n + 1).join(",")}`);
            if (!pattern.notes.length) lines.push("    (empty)");
            pattern.notes.forEach(note => {
                const pitches = note.pitches.map(n => noise ? `D${n}` : `${pitchName(n + song.key + 12)}(MIDI ${n + song.key + 12})`).join("+");
                lines.push(`    @${(note.start / 24).toFixed(3)} +${((note.end - note.start) / 24).toFixed(3)}: ${pitches}${note.continuesLastPattern ? " [continues previous bar]" : ""}; pins ${note.pins.map(pin => `${(pin.time / 24).toFixed(3)}b/bend${pin.interval}/level${pin.size}`).join(" → ")}`);
            });
        });
        if (noise && channel.instruments.some(instrument => instrument.type === 4)) lines.push(`  Standard drumset guide: ${drumNames.map((name, d) => `D${d}=${name}`).join(", ")}. Custom drum spectra can sound different.`);
        lines.push("");
    });
    lines.push("For exact edits, use the Bop score JSON (24 ticks per beat), not this rounded readout. Preserve repeated pattern references unless you intend every occurrence to change. MIDI import/export is available through File → Import/Export Song; synth timbres and some expression are MIDI approximations.");
    return lines.join("\n");
}
